import type { PostBody } from 'electron';
import type { PresentationType } from './presentation';

export interface ITab {
  id: number;
  title?: string;
  referrer?: string;
  postBody?: PostBody;
  type: TabType;
}

export interface IInnerTab extends ITab {
  url: string;
  isInnerViewLoaded?: boolean;
}

export interface IGlobalTab extends ITab {
  url?: string;
  title?: string;
  isActive?: boolean;
  activeInnerTabId?: number;
  session?: Electron.Session;
  payload?: any;
  innerTabs: IInnerTab[];
  closedInnerTabs?: IInnerTab[];
  cloneId?: string;
  demoId?: string;
  folderId?: string;
  organizationId?: string;
  workspaceId?: string;
  hidden?: boolean;
  isModalOpen?: boolean;
  hoverTitle?: string;
  favicon?: string;
  isBrowserUrlValid?: boolean;
  personaId?: string;
  group?: TabGroup;
  routeTo?: string;
  analyticsSessionId?: string;
  revisionId?: number;
  presentationId?: string;
  presentationType?: PresentationType;
  playId?: string;
  forClient?: string;
  isMobileTab?: boolean;
}

/** The purpose of GlobalTabType is to define the configuration at the beginning of the tab creation
 *  instead of relying only on tab type and then adding it to various checks and conditions in the code.
 *  This way, once configured, we should never scan the code to make sure we don't miss any condition.
 *  */
export type GlobalTabType = {
  createGlobalTabOnElectron: {
    /** Whether to start creation from src/renderer/services/TabManager.ts
     *  (by using rendererService.createGlobalTab(tab)) in parallel while
     *  creating a tab, or to wait for the tab to be created and then start creation
     *  from the component (usually within useEffect after some conditions are met
     *  or some data is loaded).
     *  */
    startOn: 'tab-creation' | 'component-load' | 'never';
    /** Whether to load interceptor for the tab. */
    withInterceptor: boolean;
    /** If we are loading our app inside another view, the api call's won't work without turning this off. */
    withWebSecurity: boolean;
  };
};

export interface IInnerTabData {
  title: string;
  url: string;
}

export enum TabType {
  BASIC = 'basic',
  CLONE = 'clone',
  CLONE_MOBILE_PREVIEW = 'mobilePreviewClone',
  UPDATE_CLONE = 'updateClone',
  PREVIEW_CLONE = 'previewClone',
  INNER = 'inner',
  OVERLAY_NEW = 'overlayNew',
  OVERLAY_EDITOR = 'overlayEditor',
  DEMO_EDITOR = 'demoEditor',
  MOBILE_DEMO_EDITOR = 'mobileDemoEditor',
  PREVIEW_OVERLAY = 'previewOverlay',
  PREVIEW_DEMO = 'previewDemo',
  PRESENT_DEMO = 'presentDemo',
  DEMO_MOBILE_PREVIEW = 'demoMobilePreview',
  DEMO_MOBILE_PRESENTATION = 'demoMobilePresentation',
  TOURS_CREATE = 'toursCreate',
  TOURS_PREVIEW = 'toursPreview',
  TOURS_EDITOR = 'toursEditor',
  LIVE_TOURS_CREATE = 'liveToursCreate',
  BROWSER_WINDOW = 'browserWindow',
  GROUP_DEMO_PRESENTATION = 'groupDemoPresentation',
  GROUP_TOUR_PRESENTATION = 'groupTourPresentation',
  GROUP_URL_PRESENTATION = 'groupUrlPresentation',
  GROUP_DEMO_PREVIEW = 'groupDemoPreview',
  GROUP_TOUR_PREVIEW = 'groupTourPreview',
  GROUP_URL_PREVIEW = 'groupUrlPreview',
  GROUP_DEMO_MOBILE_PREVIEW = 'groupDemoMobilePreview',
  GROUP_DEMO_MOBILE_PRESENTATION = 'groupDemoMobilePresentation',
  PRESENTER_DEEPLINK_DEMO = 'presenterDemoDeeplink',
  PRESENTER_DEEPLINK_PLAYBOOK = 'presenterPlaybookDeeplink',
}

export const TabTypeRumViewMap: Record<TabType, string> = {
  [TabType.BASIC]: 'basic',
  [TabType.CLONE]: 'workspace/demo/overview',
  [TabType.CLONE_MOBILE_PREVIEW]: 'workspace/clone/mobile-preview',
  [TabType.UPDATE_CLONE]: 'workspace/clone/update',
  [TabType.PREVIEW_CLONE]: 'workspace/clone/preview',
  [TabType.INNER]: 'innerTab',
  [TabType.OVERLAY_NEW]: 'workspace/folder/demo/new',
  [TabType.OVERLAY_EDITOR]: 'workspace/demo/dynamic-editor',
  [TabType.DEMO_EDITOR]: 'workspace/demo/dynamic-editor',
  [TabType.MOBILE_DEMO_EDITOR]: 'workspace/mobile-demo/dynamic-editor',
  [TabType.PREVIEW_OVERLAY]: 'workspace/demo/dynamic-preview',
  [TabType.PREVIEW_DEMO]: 'workspace/demo/dynamic-preview',
  [TabType.PRESENT_DEMO]: 'workspace/demo/presentation',
  [TabType.DEMO_MOBILE_PREVIEW]: 'workspace/mobile-demo/preview',
  [TabType.DEMO_MOBILE_PRESENTATION]: 'workspace/mobile-demo/presentation',
  [TabType.TOURS_CREATE]: 'workspace/demo/tour-capture',
  [TabType.TOURS_PREVIEW]: 'workspace/tour/preview',
  [TabType.TOURS_EDITOR]: 'workspace/tour/editor',
  [TabType.LIVE_TOURS_CREATE]: 'workspace/tour/url-capture',
  [TabType.BROWSER_WINDOW]: 'browserWindow',
  [TabType.GROUP_DEMO_PRESENTATION]: 'workspace/playbook/demo/presentation',
  [TabType.GROUP_TOUR_PRESENTATION]: 'workspace/playbook/tour/presentation',
  [TabType.GROUP_URL_PRESENTATION]: 'workspace/playbook/url/presentation',
  [TabType.GROUP_DEMO_PREVIEW]: 'workspace/playbook/clone/preview',
  [TabType.GROUP_TOUR_PREVIEW]: 'workspace/playbook/tour/preview',
  [TabType.GROUP_URL_PREVIEW]: 'workspace/playbook/url/preview',
  [TabType.GROUP_DEMO_MOBILE_PREVIEW]: 'workspace/playbook/mobile-demo/preview',
  [TabType.GROUP_DEMO_MOBILE_PRESENTATION]:
    'workspace/playbook/mobile-demo/presentation',
  [TabType.PRESENTER_DEEPLINK_DEMO]: 'presenterDemoDeeplink',
  [TabType.PRESENTER_DEEPLINK_PLAYBOOK]: 'presenterPlaybookDeeplink',
};

export interface IDemostacking {
  demostackingId?: string | number;
  currentTime: number;
  isClose: boolean;
  activePresentationIndex: number;
  isWindowsOpen: boolean;
  isNotesOpen: boolean;
  isMouseHighlightActive?: boolean;
  x: number;
  y: number;
  demosInfo: {
    presentationName: string;
    image: string;
    notes: string;
    id: number;
    screenshotImageType: 'overlay' | 'tour' | 'demo' | 'url';
  }[];
}

export type InterceptorStateKeys =
  | `global-tab-draft-rules-${number | string}`
  | `global-tab-rules-${number | string}`
  | `global-tab-edits-${number | string}`
  | `global-tab-variables-${number | string}`
  | `global-tab-token-data-${number | string}`;

export type ZoomFactor = {
  min: number;
  max: number;
  value: number;
};

export type PageDetails = {
  url: string;
  referrer?: string;
  postBody?: PostBody;
};

export type TabGroup = {
  id: number;
  name: TabGroupName;
  isVisibleIfGroupIsActive: boolean;
  isVisibleIfGroupIsInactive: boolean;
  showNonRelatedGroupTabsIfActive: boolean;
  focusOnCreate: boolean;
  closeGroupOnClose: boolean;
  mergingOptions?: {
    allowed: boolean;
    key: number;
    index: number;
    isDetached: boolean;
    detachedTitle: string;
  };
};

export enum TabGroupName {
  PlaybookPresentation = 'playbookPresentation',
  PlaybookPreview = 'playbookPreview',
  DemoPreview = 'demoPreview',
  DemoPresent = 'demoPresent',
  TourPreview = 'tourPreview',
  TourCapture = 'tourCapture',
  TourEditor = 'tourEditor',
  ClonePreview = 'clonePreview',
  DemoEditor = 'demoEditor',
  Deeplink = 'deeplink',
}
